import React from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { uploadEmailAttachments } from '../../store/actions/auth';
import {
    EMAIL_ATTACHMENT_ALLOWED_EXTENSIONS,
    EMAIL_ATTACHMENT_MAX_COUNT,
    EMAIL_ATTACHMENT_MAX_FILE_BYTES,
    EMAIL_ATTACHMENT_MAX_TOTAL_BYTES,
} from '../../constant';
import { formatFileSize, isViewable, openAttachment, toAttachment, validateAttachmentFiles } from './attachments';

const chipStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#e0e0e0',
    borderRadius: 16,
    padding: '2px 10px',
    fontSize: 13,
    marginRight: 6,
    marginBottom: 6,
};

const linkButtonStyle = {
    padding: '0 4px',
    marginLeft: 6,
    cursor: 'pointer',
    fontSize: 12,
    color: '#1976d2',
    textDecoration: 'underline',
    background: 'none',
    border: 'none',
};

// Optional attachments picker. `attachments` is the list that will be saved/sent; files are uploaded
// as soon as they pass validation, and `onUploadingChange` lets the page block Save/Send meanwhile.
const AttachmentsField = ({ attachments, onChange, onUploadingChange, disabled, templateId }) => {
    const [uploading, setUploading] = React.useState(false);
    const [errors, setErrors] = React.useState([]);
    const inputRef = React.useRef(null);
    const latestRef = React.useRef(attachments);
    latestRef.current = attachments;
    const mountedRef = React.useRef(true);
    React.useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    React.useEffect(() => {
        if (onUploadingChange) onUploadingChange(uploading);
    }, [uploading, onUploadingChange]);

    const handleFiles = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (files.length === 0) return;

        const { accepted, errors: clientErrors } = validateAttachmentFiles(files, latestRef.current);
        setErrors(clientErrors);
        if (accepted.length === 0) return;

        setUploading(true);
        try {
            const response = await uploadEmailAttachments(accepted);
            const result = response?.data?.Entity;
            if (response?.data?.Status === 200 && result) {
                const uploaded = (result.uploaded || []).map(toAttachment);
                if (uploaded.length > 0) onChange([...latestRef.current, ...uploaded]);
                setErrors([...clientErrors, ...(result.errors || [])]);
            } else {
                const message = response?.data?.Message?.AppStatusDescription || 'Upload failed, please try again';
                setErrors([...clientErrors, ...accepted.map((file) => ({ fileName: file.name, reason: message }))]);
            }
        } catch {
            setErrors([...clientErrors, ...accepted.map((file) => ({ fileName: file.name, reason: 'Upload failed, please try again' }))]);
        } finally {
            if (mountedRef.current) setUploading(false);
        }
    };

    const removeAttachment = (filePath) => {
        onChange(attachments.filter((a) => a.filePath !== filePath));
    };

    const handleOpen = async (attachment, download) => {
        const opened = await openAttachment(attachment, { download, templateId });
        if (!opened && mountedRef.current) toast.error(`Unable to ${download ? 'download' : 'open'} ${attachment.fileName}.`);
    };

    const atLimit = attachments.length >= EMAIL_ATTACHMENT_MAX_COUNT;

    return (
        <div data-testid="attachments-field">
            <input
                ref={inputRef}
                type="file"
                multiple
                hidden
                data-testid="attachments-input"
                accept={EMAIL_ATTACHMENT_ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',')}
                onChange={handleFiles}
            />
            <button
                type="button"
                className="btn cus-seconday-bg-btn"
                disabled={disabled || uploading || atLimit}
                onClick={() => inputRef.current?.click()}
            >
                {uploading ? 'Uploading...' : 'Attach Files'}
            </button>
            <span style={{ marginLeft: 12, fontSize: 12, color: '#777' }}>
                Optional. Up to {EMAIL_ATTACHMENT_MAX_COUNT} files, {formatFileSize(EMAIL_ATTACHMENT_MAX_FILE_BYTES)} each,
                {' '}{formatFileSize(EMAIL_ATTACHMENT_MAX_TOTAL_BYTES)} total ({EMAIL_ATTACHMENT_ALLOWED_EXTENSIONS.join(', ').toUpperCase()}).
            </span>

            {attachments.length > 0 && (
                <div style={{ marginTop: 10 }}>
                    {attachments.map((attachment) => (
                        <span key={attachment.filePath} style={chipStyle}>
                            {attachment.fileName} ({formatFileSize(attachment.fileSize)})
                            {isViewable(attachment.fileName) && (
                                <button type="button" style={linkButtonStyle} onClick={() => handleOpen(attachment, false)}>
                                    View
                                </button>
                            )}
                            <button type="button" style={linkButtonStyle} onClick={() => handleOpen(attachment, true)}>
                                Download
                            </button>
                            <button
                                type="button"
                                className="sortBtn"
                                aria-label={`Remove ${attachment.fileName}`}
                                disabled={disabled || uploading}
                                onClick={() => removeAttachment(attachment.filePath)}
                                style={{ padding: '4px', marginLeft: 6, cursor: 'pointer', fontSize: 16, lineHeight: 1, color: '#555' }}
                            >
                                &times;
                            </button>
                        </span>
                    ))}
                </div>
            )}

            {errors.length > 0 && (
                <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: '#d32f2f', fontSize: 13 }} role="alert">
                    {errors.map((error) => (
                        <li key={`${error.fileName}-${error.reason}`}>{error.fileName}: {error.reason}</li>
                    ))}
                </ul>
            )}
        </div>
    );
};

AttachmentsField.propTypes = {
    attachments: PropTypes.arrayOf(PropTypes.shape({
        fileName: PropTypes.string,
        fileSize: PropTypes.number,
        filePath: PropTypes.string,
    })),
    onChange: PropTypes.func.isRequired,
    onUploadingChange: PropTypes.func,
    disabled: PropTypes.bool,
    templateId: PropTypes.number,
};

AttachmentsField.defaultProps = {
    attachments: [],
    onUploadingChange: undefined,
    disabled: false,
    templateId: undefined,
};

export default AttachmentsField;
